import { Icon as LegacyIcon } from "@ant-design/compatible";
import { Link } from "react-router-dom";
import { MenuProps } from "antd";
import { MenuItem } from "../../menu/menuTypes";
import { SECTION_ORDER } from "../../menu/Menu";

type AntMenuItem = Required<MenuProps>["items"][number];

// Secciones que deben tener un Divider antes de aparecer
const SECTIONS_WITH_DIVIDER_BEFORE = new Set(["General"]);

export function buildMenuItems(
  menu: MenuItem[],
  collapsed: boolean,
  extraGroup?: AntMenuItem,
): AntMenuItem[] {
  const grouped = menu.reduce<Record<string, MenuItem[]>>((acc, item) => {
    const section = item.section || "Otros";
    acc[section] = acc[section] || [];
    acc[section].push(item);
    return acc;
  }, {});

  // Ordernar seccion de acuerdo al  SECTION_ORDER; Secciones desconocidas van al final
  const orderedSections = Object.keys(grouped).sort((a, b) => {
    const ia = SECTION_ORDER.indexOf(a);
    const ib = SECTION_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  const result: AntMenuItem[] = [];

  orderedSections.forEach((section) => {
    const sectionItems = grouped[section];
    const visibleItems = sectionItems.filter((item) => item.show !== false);

    if (visibleItems.length === 0) return;

    const groupChildren: AntMenuItem[] = visibleItems.map((item) =>
      item.submenu
        ? {
            key: item.path,
            icon: item.icon ? (
              <LegacyIcon type={item.icon} className="customized" />
            ) : undefined,
            label: collapsed ? null : item.title,
            children: item.items?.map((subItem) => ({
              key: subItem.path,
              label: (
                <Link to={subItem.path} title={subItem.title}>
                  {subItem.title}
                </Link>
              ),
            })),
          }
        : {
            key: item.path,
            icon: item.icon ? (
              <LegacyIcon type={item.icon} className="customized" />
            ) : undefined,
            label: (
              <Link to={item.path} title={item.title}>
                {item.title}
              </Link>
            ),
          },
    );

    // Agrega un grupo para el selecor de PDD en el  "Puntos de Distribución"
    if (section === "Puntos de Distribución" && extraGroup) {
      groupChildren.unshift(extraGroup);
    }

    // Agrega Divider a las secciones que lo requieren
    if (!collapsed && SECTIONS_WITH_DIVIDER_BEFORE.has(section)) {
      result.push({
        key: `divider-before-${section}`,
        type: "divider",
      } as AntMenuItem);
    }

    if (!collapsed && section !== "Otros") {
      result.push({
        key: `section-${section}`,
        label: <span className="menuSectionTitle">{section}</span>,
        type: "group",
        children: groupChildren,
      });
    } else {
      result.push(...groupChildren);
    }
  });

  return result;
}

export function findMenuItemByPath(
  menu: MenuItem[],
  pathname: string,
): MenuItem | undefined {
  const findRecursive = (items: MenuItem[]): MenuItem | undefined => {
    for (const item of items) {
      if (item.path === pathname && !item.submenu) return item;
      if (item.items) {
        const found = findRecursive(item.items);
        if (found) return found;
      }
    }
    return undefined;
  };

  const exactMatch = findRecursive(menu);
  if (exactMatch) return exactMatch;

  const flattenItems = (items: MenuItem[]): MenuItem[] =>
    items.reduce<MenuItem[]>((acc, item) => {
      acc.push(item);
      if (item.items) acc.push(...flattenItems(item.items));
      return acc;
    }, []);

  const flatMenu = flattenItems(menu);
  flatMenu.sort((a, b) => b.path.length - a.path.length);

  return flatMenu.find((item) => pathname.startsWith(item.path));
}

export function findMenuTitleByPath(
  menu: MenuItem[],
  pathname: string,
): string {
  const menuItem = findMenuItemByPath(menu, pathname);
  return menuItem?.title ?? "";
}
